import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import { Box, IconButton, Tooltip, Typography, useTheme, useMediaQuery } from "@mui/material";
import { Link } from "react-router-dom";
import "react-pro-sidebar/dist/css/styles.css";
import { tokens } from "../../theme";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BusinessIcon from "@mui/icons-material/Business";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import GroupsIcon from "@mui/icons-material/Groups";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import PaidIcon from "@mui/icons-material/Paid";
import HandshakeIcon from "@mui/icons-material/Handshake";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import DescriptionIcon from "@mui/icons-material/Description";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";

const SIDEBAR_LABEL = {
	admin: "ADMIN",
	manager: "MANAGER",
	relationship_manager: "RM",
	broker: "BROKER",
};

const Item = ({ title, to, icon, selected, setSelected, isCollapsed }) => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	return (
		<Tooltip disableHoverListener={!isCollapsed} title={title} placement="right">
			<MenuItem
				active={selected === title}
				style={{ color: colors.primary[10], marginBottom: "1px" }}
				onClick={() => setSelected(title)}
				icon={icon}
			>
				<Typography sx={{ fontSize: { xs: "14px", md: "15px" } }} fontWeight={600}>
					{title}
				</Typography>
				<Link to={to} />
			</MenuItem>
		</Tooltip>
	);
};

const BROKER_NAV = [
	{ title: "Commissions Due", to: "/broker-commissions", icon: <PaidIcon /> },
	{ title: "Assigned Landlords", to: "/broker-landlords", icon: <HandshakeIcon /> },
	{ title: "Payment History", to: "/broker-payments", icon: <ReceiptIcon /> },
];

const ADMIN_NAV = [
	{ title: "Dashboard", to: "/home", icon: <HomeOutlinedIcon /> },
	{ title: "Organisations", to: "/organisations", icon: <CorporateFareIcon /> },
	{ title: "Reallocate Landlords", to: "/reallocate-landlords", icon: <SwapHorizIcon /> },
	{ title: "Payment Charges", to: "/payment-charges", icon: <PaidIcon /> },
	{ title: "Agreement Templates", to: "/agreement-templates", icon: <DescriptionIcon /> },
	{ title: "Notifications", to: "/notifications", icon: <NotificationsActiveIcon /> },
	{ title: "Audit & Approvals", to: "/audit-approvals", icon: <FactCheckIcon /> },
	{ title: "Accounting", to: "/accounting", icon: <AccountBalanceIcon /> },
	{ title: "Bookings", to: "/bookings", icon: <HomeWorkIcon /> },
	{ title: "Reports", to: "/reports", icon: <AssessmentIcon /> },
];

const STAFF_BASE_NAV = [
	{ title: "Dashboard", to: "/home", icon: <HomeOutlinedIcon /> },
	{ title: "Landlords", to: "/landlords", icon: <BusinessIcon /> },
	{ title: "Properties", to: "/generalProperties", icon: <HomeWorkIcon /> },
];

const MANAGER_ONLY_NAV = [
	{ title: "Property Manager", to: "/managment", icon: <ApartmentIcon /> },
	{ title: "Relationship Managers", to: "/relationship-managers", icon: <SupportAgentIcon /> },
	{ title: "Users", to: "/tenants", icon: <GroupsIcon /> },
];

const STAFF_SHARED_NAV = [
	{ title: "Audit & Approvals", to: "/audit-approvals", icon: <FactCheckIcon /> },
	{ title: "Accounting", to: "/accounting", icon: <AccountBalanceIcon /> },
];

const MANAGER_EXTRA_NAV = [
	{ title: "Bookings", to: "/bookings", icon: <HomeWorkIcon /> },
	{ title: "Anonymous Tenancies", to: "/anonymous-tenancies", icon: <PrivacyTipIcon /> },
	{ title: "Anonymous Landlords", to: "/anonymous-landlords", icon: <SwitchAccountIcon /> },
];

const PAYMENTS_NAV = { title: "Payments", to: "/payments", icon: <ReceiptIcon /> };

const STAFF_TAIL_NAV = [{ title: "Reports", to: "/reports", icon: <AssessmentIcon /> }];

const MANAGER_FOOTER_NAV = [
	{ title: "Broker Commissions", to: "/broker-commission-payouts", icon: <PaidIcon /> },
	{ title: "Settings", to: "/settings", icon: <SettingsIcon /> },
];

const getNavItems = (role) => {
	if (role === "broker") return BROKER_NAV;
	if (role === "admin") return ADMIN_NAV;

	const items = [...STAFF_BASE_NAV];
	if (role === "manager") items.push(...MANAGER_ONLY_NAV);
	items.push(PAYMENTS_NAV);
	items.push(...STAFF_SHARED_NAV);
	if (role === "manager") items.push(...MANAGER_EXTRA_NAV);
	items.push(...STAFF_TAIL_NAV);
	if (role === "manager") items.push(...MANAGER_FOOTER_NAV);
	return items;
};

const Sidebar = ({ isCollapsed, handleIsCollapsed }) => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);
	const isNonMobileScreens = useMediaQuery("(min-width: 900px)");
	const [selected, setSelected] = useState("Dashboard");
	const roleFromState = useSelector((state) => state.role);
	const isAdminFlag = useSelector((state) => state.isAdmin);
	const role = roleFromState || (isAdminFlag === 1 ? "admin" : "manager");

	const navItems = useMemo(() => getNavItems(role), [role]);
	const sideBarPosition = isNonMobileScreens ? "sticky" : "absolute";
	const showSideBar = !isNonMobileScreens && isCollapsed ? { display: "none" } : {};

	return (
		<Box
			sx={{
				position: sideBarPosition,
				height: "100vh",
				"& .pro-sidebar-inner": {
					background: `${colors.primary[10]} !important`,
				},
				"& .pro-icon-wrapper": {
					backgroundColor: "transparent !important",
				},
				"& .pro-inner-item": {
					padding: { xs: "5px 15px 5px 10px !important", sm: "5px 35px 5px 20px !important" },
				},
				"& .pro-menu-item": {
					color: "#333 !important",
				},
				"& .pro-inner-item:hover": {
					color: "#FCB200 !important",
				},
				"& .pro-menu-item.active": {
					color: "#FCB200 !important",
				},
				borderRight: "1px solid #dfdede;",
				...showSideBar,
			}}
		>
			<ProSidebar collapsed={isCollapsed}>
				<Menu iconShape="square">
					<MenuItem
						onClick={() => handleIsCollapsed()}
						icon={isCollapsed ? <MenuOutlinedIcon /> : undefined}
						style={{
							margin: "10px 0 20px 0",
							color: colors.grey[10],
						}}
					>
						{!isCollapsed && (
							<Box display="flex" justifyContent="space-between" alignItems="center" ml="15px">
								<Typography variant="h4" color={colors.grey[10]}>
									{SIDEBAR_LABEL[role] ?? "ADMIN"}
								</Typography>
								<IconButton onClick={() => handleIsCollapsed()}>
									<MenuOutlinedIcon sx={{ color: colors.grey[10] }} />
								</IconButton>
							</Box>
						)}
					</MenuItem>

					<Box paddingLeft={isCollapsed ? undefined : "1%"}>
						{navItems.map((item) => (
							<Item
								key={item.to}
								title={item.title}
								to={item.to}
								icon={item.icon}
								selected={selected}
								setSelected={setSelected}
								isCollapsed={isCollapsed}
							/>
						))}
					</Box>
				</Menu>
			</ProSidebar>
		</Box>
	);
};

export default Sidebar;
